import { clone } from 'lodash';
import rawSchema from './schema.json';
import type { Schema as SchemaType, CreatorType } from './types';

class SchemaManager {
  private schema: SchemaType;
  readonly CSLFields: { [key: string]: string[] };

  constructor() {
    // Assert the imported schema matches our type definition
    this.schema = rawSchema as unknown as SchemaType;
    
    // Initialize CSLFields after schema is set
    this.CSLFields = {
      ...this.schema.csl.fields.text,
      ...this.arrayifyFields(this.schema.csl.fields.date),
      ...this.arrayifyFields(this.schema.csl.names),
    };
  }

  private arrayifyFields(obj: { [key: string]: string }): { [key: string]: string[] } {
    const result: { [key: string]: string[] } = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = [value];
    }
    return result;
  }

  getItemTypes(locale: string = 'fr-FR'): Array<{ label: string; value: string }> {
    return this.schema.itemTypes.map(type => ({
      label: this.getItemTypeLabel(type.itemType, locale),
      value: type.itemType,
    }));
  }

  getItemTypeFields(
    itemType: string | null,
    withBaseField: boolean = false,
    required: boolean | null = null
  ): Array<any> {
    if (!itemType) return [];

    const typeInfo = this.schema.itemTypes.find(type => type.itemType === itemType);
    if (!typeInfo) return [];

    return typeInfo.fields
      .filter(field => {
        if (required === null) return true;
        return required ? field.required : !field.required;
      })
      .map(field => (withBaseField ? field : field.field));
  }

  getItemTypeLabel(itemType: string, locale: string = 'fr-FR'): string {
    return this.schema.locales[locale]?.itemTypes[itemType] ?? itemType;
  }

  getFieldLabel(field: string, locale: string = 'fr-FR'): string {
    const localeData = this.schema.locales[locale];
    if (!localeData) return field;
    return { ...localeData.fields, ...localeData.creatorTypes }[field] ?? field;
  }

  getBaseField(field: string): string {
    for (const itemType of this.schema.itemTypes) {
      for (const currentField of itemType.fields) {
        if (field === currentField.field) {
          return currentField.baseField ?? currentField.field;
        }
      }
    }
    return field;
  }

  hasCreator(itemType: string): boolean {
    const typeInfo = this.schema.itemTypes.find(type => type.itemType === itemType);
    return (typeInfo?.creatorTypes ?? []).length > 0;
  }

  getCreatorTypes(itemType: string): CreatorType[] {
    const typeInfo = this.schema.itemTypes.find(type => type.itemType === itemType);
    return typeInfo?.creatorTypes ?? [];
  }

  toCSLFields(fields: { [key: string]: any }, itemType: string): { [key: string]: any } {
    const fieldsCopy = clone(fields);
    
    for (const [field, value] of Object.entries(fieldsCopy)) {
      const cslField = this.getCSLField(field, itemType);
      if (!cslField || !value) {
        console.warn(`Unknown field: ${field}`);
        delete fieldsCopy[field];
        continue;
      }
      
      fieldsCopy[cslField] = value;
      if (cslField !== field) {
        delete fieldsCopy[field];
      }
    }
    
    return fieldsCopy;
  }

  getCSLType(itemType: string): string | null {
    for (const [cslType, itemTypes] of Object.entries(this.schema.csl.types)) {
      if (itemTypes.includes(itemType)) {
        return cslType;
      }
    }
    return null;
  }

  getCSLField(field: string, itemType: string): string | null {
    const cslFields = Object.entries(this.CSLFields);
    for (const [cslField, fields] of cslFields) {
      if (fields.includes(this.getBaseField(field))) {
        return cslField;
      }
    }
    return null;
  }

  getSchemaType(cslType: string): string {
    return this.schema.csl.types[cslType]?.[0] ?? cslType;
  }

  getSchemaField(cslField: string, itemType: string): string {
    const fields = this.getItemTypeFields(itemType, true);
    const possibleFields = this.CSLFields[cslField] ?? [];

    for (const field of possibleFields) {
      for (const itemField of fields) {
        if (itemField.baseField === field) {
          return itemField.field;
        }
        if (itemField.field === field) {
          return field;
        }
      }
    }
    
    return this.CSLFields[cslField]?.[0] ?? cslField;
  }

  toSchemaFields(fields: { [key: string]: any }, itemType: string): { [key: string]: any } {
    const fieldsCopy = clone(fields);
    
    for (const [field, value] of Object.entries(fieldsCopy)) {
      const schemaField = this.getSchemaField(field, itemType);
      if (!schemaField) {
        console.warn(`Unknown field: ${field}`);
        delete fieldsCopy[field];
        continue;
      }
      
      fieldsCopy[schemaField] = value;
      if (schemaField !== field) {
        delete fieldsCopy[field];
      }
    }
    
    return fieldsCopy;
  }
}

export const schemaManager = new SchemaManager();
export default schemaManager;